import { Expose } from 'class-transformer';

export type PccObservationType = 'weight' | 'respirations' | 'bloodPressure' | 'temperature' | 'heartrate' | 'bloodSugar' | 'height' | 'oxygenSaturation' | 'painLevel';

export class PccObservationParams {
	types?: PccObservationType[];

	recordedStartDateTime?: string;

	recordedEndDateTime?: string;

	latestOnly?: boolean;
}

/**
 * Represents a warning related to a patient observation
 */
export class PccObservationWarning {
	/**
	 * Description of the warning
	 */
	description: string;

	/**
	 * Indicates whether the warning has been cleared
	 */
	cleared: boolean;

	constructor(partial?: Partial<PccObservationWarning>) {
		Object.assign(this, partial);
	}

	getDetails() {
		return `Warning: ${this.description}, Cleared: ${this.cleared}`;
	}
}

/**
 * Represents a patient observation in PointClickCare
 *
 * SimpliScreen maps the observation to the following string for embedding in the RAG:
 * type: {type}, value: {value}, unit: {unit}, recordedDate: {recordedDate}, recordedBy: {recordedBy}, method: {method}
 * \t Warnings: description: {description}, cleared: {cleared}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/observations?patientId={patientId}
 */
export class PccObservation {
	/**
	 * A unique identifier for the hardware device used to record the observation. This field will be available only if the observation was created through a third party application.
	 */
	deviceId?: string;

	/**
	 * Display name for the device used to record the observation. This field will be available only if the observation was created through a third party application.
	 */
	deviceName?: string;

	/**
	 * The recorded diastolic Blood Pressure for the given patient.
	 */
	diastolicValue?: number;

	/**
	 * Represents the method used to record the observation. This field is applicable for Heart Rate, Blood Pressure, Weight, Height, Oxygen Saturation and Pain Level.
	 */
	method?: string;

	/**
	 * The unique identifier for the observation.
	 */
	observationId: number;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 */
	patientId: number;

	/**
	 * The user who recorded the observation.
	 */
	recordedBy: string;

	/**
	 * Date and time when the observation was recorded. The Date and Time should be expressed in UTC in the following format YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	recordedDate: string;

	/**
	 * The recorded systolic Blood Pressure for the given patient.
	 */
	systolicValue?: number;

	/**
	 * Represents the type of observation. Acceptable values are heartrate, bloodPressure, bloodSugar, oxygenSaturation, weight, height, painLevel, temperature, respirations.
	 */
	type: 'weight' | 'respirations' | 'bloodPressure' | 'temperature' | 'heartrate' | 'bloodSugar' | 'height' | 'oxygenSaturation' | 'painLevel';

	/**
	 * Represents the unit of measure for the observation.
	 */
	unit: string;

	/**
	 * Unit code information from standardized coding systems
	 */
	unitCode?: {
		code: string;
		system: string;
	};

	/**
	 * Method code information from standardized coding systems (e.g., LOINC)
	 */
	methodCode?: {
		codings: Array<{
			system: string;
			version: string;
			code: string;
			display: string;
		}>;
	};

	/**
	 * Recorded reading for the observation.
	 */
	value: number;

	/**
	 * List of warnings related to patient's observation reading.
	 */
	warnings?: PccObservationWarning[];

	/**
	 * Represents the details of the observation.
	 *
	 * SimpliScreen maps the observation to the following string for embedding in the RAG:
	 * type: {type}, value: {value}, unit: {unit}, recordedDate: {recordedDate}, recordedBy: {recordedBy}, method: {method}
	 * \t Warnings: description: {description}, cleared: {cleared}
	 */
	details: string;

	constructor(partial?: Partial<PccObservation>) {
		Object.assign(this, partial);
		if (this.warnings) {
			this.warnings = this.warnings.map((warning) => new PccObservationWarning(warning));
		}
	}

	@Expose({ name: 'details' })
	getDetails(skipType = false) {
		const details = [`Data Type: Observation`];
		if (this.observationId) details.push(`Observation ID: ${this.observationId}`);
		if (this.type && !skipType) details.push(`Type: ${this.type}`);
		if (this.value !== undefined) details.push(`Value: ${this.value}`);
		if (this.unit) details.push(`Unit: ${this.unit}`);
		if (this.recordedDate) details.push(`Recorded Date: ${this.recordedDate}`);
		if (this.recordedBy) details.push(`Recorded By: ${this.recordedBy}`);
		if (this.method) details.push(`Method: ${this.method}`);
		if (this.systolicValue !== undefined) details.push(`Systolic: ${this.systolicValue}`);
		if (this.diastolicValue !== undefined) details.push(`Diastolic: ${this.diastolicValue}`);

		let warningsStr = '';
		if (this.warnings && this.warnings.length > 0) {
			warningsStr = `\n${this.warnings.map((warning) => `\t${warning.getDetails()}`).join('\n')}`;
		}

		return `${details.join(', ')}${warningsStr}`;
	}
}
